import NavBar from "@/components/woost/NavBar";
import Footer from "@/components/woost/Footer";
import { ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

const Terms = (): JSX.Element => {
  return (
    <div className="min-h-screen bg-white">
      <NavBar />

      <motion.div
        className="container mx-auto px-4 py-20"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to="/"
          className="inline-flex items-center text-woost-blue mb-8 hover:underline"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold mb-8">Terms & Conditions</h1>

        <div className="prose prose-lg max-w-none">
          <p>Last Updated: May 17, 2025</p>

          <h2>1. Introduction</h2>
          <p>
            Welcome to Woost! These Terms and Conditions ("Terms") govern your
            use of the Woost mobile application and website (collectively, the
            "Platform") operated by Woost Internet Private Limited ("we," "us,"
            or "our"). By accessing or using our Platform, you agree to be bound
            by these Terms. If you disagree with any part of these Terms, you
            may not access the Platform.
          </p>

          <h2>2. Definitions</h2>
          <p>
            "Brand" refers to any business entity or individual who uses our
            Platform to connect with Creators for marketing campaigns.
          </p>
          <p>
            "Creator" refers to any individual who uses our Platform to create
            content for Brands through partnerships facilitated by Woost.
          </p>
          <p>
            "Campaign" refers to any marketing initiative set up by Brands on
            our Platform that Creators can apply to participate in.
          </p>
          <p>
            "Content" refers to any material created by Creators for Brands,
            including but not limited to photos, videos, reviews, and posts on
            social media platforms.
          </p>

          <h2>3. Account Registration</h2>
          <p>
            To access certain features of the Platform, you must register for an
            account. You agree to provide accurate, current, and complete
            information during the registration process and to update such
            information to keep it accurate, current, and complete.
          </p>
          <p>
            You are responsible for maintaining the confidentiality of your
            account credentials and for all activities that occur under your
            account. You must immediately notify us of any unauthorized use of
            your account or any other breach of security.
          </p>

          <h2>4. User Conduct</h2>
          <p>As a user of our Platform, you agree not to:</p>
          <ul>
            <li>Violate any applicable laws or regulations</li>
            <li>Infringe upon the rights of others</li>
            <li>Provide false or misleading information</li>
            <li>Upload or transmit viruses or malicious code</li>
            <li>Attempt to gain unauthorized access to the Platform</li>
            <li>Use the Platform for any illegal or unauthorized purpose</li>
            <li>Interfere with or disrupt the operation of the Platform</li>
          </ul>

          <h2>5. Intellectual Property</h2>
          <p>
            The Platform and its original content, features, and functionality
            are owned by Woost and are protected by international copyright,
            trademark, patent, trade secret, and other intellectual property
            laws.
          </p>
          <p>
            By creating and uploading Content as a Creator, you grant the Brand
            a non-exclusive, worldwide, royalty-free license to use, reproduce,
            distribute, and display such Content for the purposes agreed upon in
            the Campaign.
          </p>
          <p>
            Brands retain their intellectual property rights in their
            trademarks, logos, and product designs. Creators may only use these
            as specifically authorized for the Campaign.
          </p>

          <h2>6. Payments</h2>
          <p>
            Brands agree to pay Creators the agreed-upon compensation for their
            services through our Platform. We may charge service fees for
            facilitating these transactions.
          </p>
          <p>
            All payments are processed securely through our Platform. Once a
            Brand approves a Creator's work, payment will be processed according
            to the terms specified in the Campaign.
          </p>

          <h2>7. Cancellation and Refunds</h2>
          <p>
            Cancellation policies vary depending on the stage of the Campaign.
            Refunds may be issued if a Creator fails to fulfill their
            obligations as outlined in the Campaign brief.
          </p>
          <p>
            In case of disputes, our team will review the situation and make a
            determination in accordance with our policies.
          </p>

          <h2>8. Privacy</h2>
          <p>
            Your privacy is important to us. Our Privacy Policy describes how we
            collect, use, and share information about you. By using our
            Platform, you agree to the terms of our Privacy Policy.
          </p>

          <h2>9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Woost shall not be liable
            for any indirect, incidental, special, consequential, or punitive
            damages, including without limitation, loss of profits, data, use,
            goodwill, or other intangible losses, resulting from your access to
            or use of or inability to access or use the Platform.
          </p>

          <h2>10. Indemnification</h2>
          <p>
            You agree to indemnify, defend, and hold harmless Woost and its
            officers, directors, employees, agents, and affiliates from and
            against any and all claims, liabilities, damages, losses, costs,
            expenses, or fees (including reasonable attorneys' fees) arising
            from your violation of these Terms or your use of the Platform.
          </p>

          <h2>11. Modifications</h2>
          <p>
            We reserve the right to modify or replace these Terms at any time.
            If a revision is material, we will provide at least 30 days' notice
            prior to any new terms taking effect. We will determine what
            constitutes a material change in our sole discretion.
          </p>

          <h2>12. Termination</h2>
          <p>
            We may terminate or suspend your account immediately, without prior
            notice or liability, for any reason whatsoever, including without
            limitation if you breach these Terms.
          </p>
          <p>
            Upon termination, your right to use the Platform will immediately
            cease. If you wish to terminate your account, you may simply
            discontinue using the Platform.
          </p>

          <h2>13. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with
            the laws of India, without regard to its conflict of law provisions.
          </p>

          <h2>14. Contact Us</h2>
          <p>
            If you have any questions about these Terms, please contact us at:
          </p>
          <p>
            Woost Internet Private Limited
            <br />
            H-102, Third floor, Sector 63 Rd, H Block, Sector 63, Noida, Uttar
            Pradesh 201301
            <br />
            Email: partners@woost.io
          </p>
        </div>
      </motion.div>

      <Footer />
    </div>
  );
};

export default Terms;
